import currentLibrary from '../data/default-monsters.json';
import previousV0Library from '../data/default-monsters-v0.json';
import previousV1Library from '../data/default-monsters-v1.json';
import { CREATURE_MECHANICS_VERSION, validateCreatureInventory } from './creatureAbilities';

type RecordValue = Record<string, any>;
const copy = <T>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value));
const equal = (left: any, right: any): boolean => JSON.stringify(left) === JSON.stringify(right);
const isRecord = (value: any): value is RecordValue => value !== null && typeof value === 'object' && !Array.isArray(value);
const buckets = ['actions', 'bonusActions', 'reactions', 'traits'];
const fields = [...buckets, 'resistanceBlock', 'conditionImmunities', 'savingThrows', 'equipmentState'];
const displayFields = new Set(['name', 'text', 'description', 'notes', 'ruleReference']);
const legacyNormalizationDroppedFields = new Set(['activation', 'activationType', 'conditionImmunities']);

export interface DefaultCreatureMigrationResult {
  data: RecordValue;
  changed: boolean;
  status: 'current' | 'migrated' | 'blocked' | 'not-default';
  fromVersion: string;
  toVersion: string;
  conflicts: string[];
}

/** Historical adapter projection, used ONLY by the persisted v0 migration.
 * It documents which fields that adapter dropped; combat never calls it.
 * date_of_change: 2026-09-05
 */
const projectedV0Entry = (entry: RecordValue): RecordValue => {
  const attack = entry.attack;
  // The historical library adapter stored display distances as strings.
  // Migration retains that boundary shape, not a combat-time conversion.
  // date_of_change: 2026-09-05
  const displayDistance = (value: any) => typeof value === 'number' ? `${value} ft` : value;
  const kinds: Record<string, string> = {
    meleeWeapon: 'meleeWeaponAttack', rangedWeapon: 'rangedWeaponAttack',
    meleeSpell: 'meleeSpellAttack', rangedSpell: 'rangedSpellAttack'
  };
  const result: RecordValue = {
    name: entry.name,
    kind: entry.kind || kinds[attack?.type] || 'ability',
    ...(attack ? {
      toHit: attack.toHit, reach: displayDistance(attack.reach),
      range: attack.range ? { short: displayDistance(attack.range.normal), long: displayDistance(attack.range.long) } : undefined,
      damage: copy(attack.damage),
    } : {}),
    text: attack?.notes || entry.text || entry.description,
  };
  for (const key of ['standardActionKey', 'executionRoute', 'targetRequirement', 'ruleReference', 'featureKey']) {
    if (entry[key] !== undefined) result[key] = copy(entry[key]);
  }
  return JSON.parse(JSON.stringify(result));
};

/**
 * Three-way content migration. Runtime instance state is never a merge input:
 * only the mechanical inventory changes. Conflicting mechanical edits block
 * the migration instead of being overwritten or guessed from prose.
 * Rule reference: AGENTS.md canonical data preservation. date_of_change: 2026-09-05
 */
export function migrateDefaultCreatureData(data: RecordValue, templateId: string): DefaultCreatureMigrationResult {
  const next = (currentLibrary.monsters as RecordValue[]).find(entry => entry.id === templateId)?.data;
  const fromVersion = data.contentVersion || 'v0';
  const result: DefaultCreatureMigrationResult = {
    data: copy(data), changed: false, status: 'not-default',
    fromVersion, toVersion: CREATURE_MECHANICS_VERSION, conflicts: []
  };
  if (!next) return result;
  if (data.contentVersion === CREATURE_MECHANICS_VERSION) {
    try { validateCreatureInventory(data, templateId); result.status = 'current'; }
    catch (error) { result.status = 'blocked'; result.conflicts.push(error instanceof Error ? error.message : String(error)); }
    return result;
  }
  // Each maintained version is an explicit three-way baseline. Equipment
  // changes made to an instance are preserved while new required fields are
  // added from the next source document. date_of_change: 2026-09-06
  const sourceLibrary = fromVersion === 'v0'
    ? previousV0Library
    : fromVersion === 'srd-5.1-mechanics-1' ? previousV1Library : null;
  const old = sourceLibrary
    ? ((sourceLibrary as any).monsters as RecordValue[]).find(entry => entry.id === templateId)?.data
    : null;
  if (!old) {
    result.status = 'blocked';
    result.conflicts.push(`${templateId}.contentVersion: no migration from ${fromVersion}.`);
    return result;
  }

  const merge = (before: any, present: any, after: any, path: string): any => {
    if (equal(present, before)) return copy(after);
    // Some pre-versioned browser templates were normalized before activation
    // metadata and condition immunities were retained. Those records look like
    // explicit deletions when compared with the historical source even though
    // the user never edited them. Restore only fields known to have been lost
    // at the unversioned v0 boundary; display text and every other mechanical
    // edit keep the normal three-way conflict behavior.
    // date_of_change: 2026-09-07
    const fieldName = path.split('.').pop() || '';
    if (
      fromVersion === 'v0'
      && present === undefined
      && before !== undefined
      && equal(before, after)
      && legacyNormalizationDroppedFields.has(fieldName)
    ) return copy(after);
    if (equal(before, after)) return copy(present);
    if (present === undefined && before === undefined) return copy(after);
    if (before === undefined) return copy(present);
    if (present === undefined) return undefined; // An explicit deletion is an edit.
    if (isRecord(before) && isRecord(present) && isRecord(after)) {
      const merged = copy(present);
      for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (displayFields.has(key) && !equal(present[key], before[key])) continue;
        const value = merge(before[key], present[key], after[key], `${path}.${key}`);
        if (value === undefined) delete merged[key]; else merged[key] = value;
      }
      return merged;
    }
    if (!displayFields.has(path.split('.').pop() || '')) result.conflicts.push(path);
    return copy(present);
  };

  for (const field of fields) {
    if (!buckets.includes(field)) {
      result.data[field] = merge(old[field], data[field], next[field], `${templateId}.${field}`);
      continue;
    }
    if (!Array.isArray(data[field])) {
      result.data[field] = copy(next[field]);
      continue;
    }
    const baseline = Array.isArray(old[field]) ? old[field] : [];
    const upgraded = next[field] as RecordValue[];
    const used = new Set<number>();
    const rows = data[field].map((entry: RecordValue, index: number) => {
      const candidates = baseline.map((row: RecordValue, oldIndex: number) => ({ row, oldIndex }))
        .filter(({ row, oldIndex }: { row: RecordValue; oldIndex: number }) => !used.has(oldIndex) && (row.name === entry.name || (entry.id && entry.id === upgraded.find(value => value.name === row.name)?.id)));
      if (candidates.length !== 1) {
        // Already structured homebrew additions are retained, not replaced.
        if (!entry.mechanics) result.conflicts.push(`${templateId}.${field}[${index}]: no unique v0 identity; confirm this edited entry.`);
        return copy(entry);
      }
      const { row, oldIndex } = candidates[0];
      used.add(oldIndex);
      const authoredReplacement = upgraded.find(value => value.name === row.name);
      if (!authoredReplacement) return copy(entry);
      const replacement = field !== 'traits' && entry.kind
        ? { ...projectedV0Entry(authoredReplacement), ...authoredReplacement }
        : authoredReplacement;
      const previous = field !== 'traits' && entry.kind && !entry.attack ? projectedV0Entry(row) : row;
      return merge(previous, entry, replacement, `${templateId}.${field}[${index}]`);
    });
    // Only genuinely new source entries are appended. Removed user entries
    // stay removed and are caught by reference/readiness validation if needed.
    upgraded.filter(entry => !baseline.some((row: RecordValue) => row.name === entry.name))
      .forEach(entry => {
        if (!rows.some((row: RecordValue) => row.id === entry.id)) {
          rows.push(copy(field !== 'traits' && data[field].some((row: RecordValue) => row.kind)
            ? { ...projectedV0Entry(entry), ...entry } : entry));
        }
      });
    result.data[field] = rows;
  }
  // SRD 5.1 saving throws include the ability modifier. When the historical
  // file had no total, derive the new total from its preserved edited score,
  // retaining the source's proficiency contribution. Explicit totals/deletions
  // remain user edits. This happens only at migration. date_of_change: 2026-09-05
  if (isRecord(result.data.savingThrows)) {
    const abilityKeys: Record<string, string> = {
      strength: 'str', dexterity: 'dex', constitution: 'con',
      intelligence: 'int', wisdom: 'wis', charisma: 'cha'
    };
    for (const [ability, short] of Object.entries(abilityKeys)) {
      if (data.savingThrows?.[ability] !== undefined || old.savingThrows?.[ability] !== undefined) continue;
      const beforeScore = old.abilities?.[ability] ?? old.abilities?.[short];
      const presentScore = data.abilities?.[ability] ?? data.abilities?.[short];
      if (!Number.isInteger(beforeScore) || !Number.isInteger(presentScore) || presentScore < 1 || presentScore > 30) {
        result.conflicts.push(`${templateId}.abilities.${ability}: provide an integer score from 1 to 30 before deriving saving throws.`);
        continue;
      }
      result.data.savingThrows[ability] = next.savingThrows[ability]
        + Math.floor((presentScore - 10) / 2) - Math.floor((beforeScore - 10) / 2);
    }
  }
  result.data.contentVersion = CREATURE_MECHANICS_VERSION;
  try { validateCreatureInventory(result.data, templateId); }
  catch (error) { result.conflicts.push(error instanceof Error ? error.message : String(error)); }
  if (result.conflicts.length) {
    result.data = copy(data);
    result.status = 'blocked';
  } else {
    result.status = 'migrated';
    result.changed = true;
  }
  return result;
}

/** Migrate saved payloads before persistence, never in a combat selector.
 * Instance IDs, HP, conditions, flags, resources and geometry are untouched.
 * A blocked instance keeps its exact payload and an explicit remediation report.
 * date_of_change: 2026-09-05
 */
export function migrateDefaultCreatureScene(state: RecordValue): { changed: boolean; reports: RecordValue[] } {
  let changed = false;
  const reports: RecordValue[] = [];
  for (const token of state.map?.tokens || state.tokens || []) {
    const entityReference = isRecord(token.entityRef) ? token.entityRef : null;
    if (token.isCustom === true || entityReference?.sourceKind === 'custom') continue;
    const canonicalReference = entityReference?.sourceKind === 'template'
      ? entityReference.canonicalId || entityReference.entityId
      : null;
    const reference = token.monsterDataRef || canonicalReference;
    let payload = token.entityPayload || token.monsterData || (reference ? state.templateMap?.[reference] || state.map?.snapshotTemplates?.[reference] || state.map?.templateMap?.[reference] : null);
    let data = payload?.data || payload;
    const templateId = token.monsterId || token.baseTemplate || canonicalReference || payload?.id;
    const isMonster = token.type === 'monster'
      || entityReference?.entityType === 'monster'
      || Boolean(token.monsterId || token.monsterDataRef || token.monsterData);
    let hydratedMissingPayload = false;

    // Older saved rooms may retain only an explicit built-in monsterId or the
    // compact snapshot entityRef. At this persistence boundary, hydrate that
    // exact maintained template; never infer from a display name. Runtime
    // instance fields remain on the token.
    // date_of_change: 2026-09-07
    if (!data && isMonster) {
      const maintained = templateId
        ? (currentLibrary.monsters as RecordValue[]).find(entry => entry.id === templateId)?.data
        : null;
      if (!maintained) {
        reports.push({
          instanceId: token.id,
          templateId: templateId || null,
          status: 'blocked',
          fromVersion: 'missing-payload',
          toVersion: CREATURE_MECHANICS_VERSION,
          conflicts: [templateId
            ? `${token.id}.monsterId: "${templateId}" is not a maintained built-in and no embedded/template payload exists. Restore the exact source payload before combat. See Character_Monster_Data_Architecture.md.`
            : `${token.id}.monsterId: provide an explicit maintained built-in ID or embed the complete monster payload before combat. See Character_Monster_Data_Architecture.md.`]
        });
        continue;
      }
      payload = copy(maintained);
      data = payload;
      hydratedMissingPayload = true;
    }
    if (!data || !templateId) continue;
    const overlay = token.runtimeOverlay || token;
    // Migrate the actual instance inventory, not just its original sheet.
    // Conflicts leave both containers unchanged. date_of_change: 2026-09-05
    const instanceData = { ...copy(data) };
    for (const field of [...fields, 'contentVersion']) {
      if (overlay[field] !== undefined) instanceData[field] = copy(overlay[field]);
    }
    const report = migrateDefaultCreatureData(instanceData, templateId);
    if (report.status === 'not-default' || (report.status === 'current' && !hydratedMissingPayload)) continue;
    const effectiveReport = hydratedMissingPayload
      ? { ...report, status: 'migrated', changed: true, fromVersion: 'missing-payload' }
      : report;
    reports.push({ instanceId: token.id, templateId, status: effectiveReport.status, fromVersion: effectiveReport.fromVersion, toVersion: effectiveReport.toVersion, conflicts: effectiveReport.conflicts });
    if (!effectiveReport.changed) continue;
    const migratedPayload = payload.data ? { ...copy(payload), data: report.data } : report.data;
    if (token.entityPayload) token.entityPayload = migratedPayload;
    else if (token.monsterData) token.monsterData = migratedPayload;
    else {
      // Embed per-instance snapshots instead of mutating a shared template map.
      token.entityPayload = migratedPayload;
      delete token.monsterDataRef;
    }
    for (const field of [...fields, 'contentVersion']) {
      overlay[field] = copy(report.data[field]);
    }
    changed = true;
  }
  if (reports.length && !equal(state.defaultCreatureMigration, reports)) {
    state.defaultCreatureMigration = reports;
    changed = true;
  }
  return { changed, reports };
}
